import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import type { NodonIcons } from '@ui/Icon/Icon';
import { SBEF_OFFSET_BOX_RATIO } from './report-product-groups.constants';
import type {
  ProductGroupSegment,
  ProductGroupsDonutCenter,
} from './report-product-groups.model';

const OFFSET_BOX_TABULAR_CHAR_WIDTH_RATIO = 0.58;

const OVRIGA_MAX_SHARE = 0.08;
const OVRIGA_MIN_SHARE = 0.01;
export const OVRIGA_SEGMENT_ID = 'ovriga-grouped';
export const OVRIGA_SEGMENT_LABEL = 'Other';

/** Icon for a product-groups legend / donut segment. */
export function getProductGroupSegmentIconName(
  segmentId: ProductGroupSegment['id'],
): keyof NodonIcons['elementCategory'] {
  return getElementIconCategoryName(segmentId as ElementCategoryID);
}

/**
 * Text drawn in the donut hole.
 * @returns The delta label, or `undefined` when {@link ProductGroupsDonutCenter} is `none`.
 */
export const getProductGroupsDonutCenterLabel = (
  center: ProductGroupsDonutCenter,
): string | undefined => {
  switch (center.kind) {
    case 'delta':
      return center.text;
    case 'none':
      return undefined;
    default:
      return assertUnreachable(center);
  }
};

/** Pie-chart arrow values and offset-box deltas — 0 decimals when value ≥ 1, else 1. */
export function formatProductGroupsChartValue(value: number): string {
  return getDisplayNumber(value, { decimals: value >= 1 ? 0 : 1 });
}

function estimateOffsetBoxTextWidth(text: string, fontSize: number): number {
  return text.length * fontSize * OFFSET_BOX_TABULAR_CHAR_WIDTH_RATIO;
}

function estimateOffsetBoxFixedWidth(fontSize: number): number {
  return (
    fontSize * SBEF_OFFSET_BOX_RATIO.iconHeight +
    fontSize * SBEF_OFFSET_BOX_RATIO.paddingRight +
    fontSize * SBEF_OFFSET_BOX_RATIO.borderWidth * 2
  );
}

/** OffsetBox width in scaling units at {@link widthScaleReference}. */
export function estimateOffsetBoxBaseWidth(
  widthScaleReference: number,
  fontSize: number,
): number {
  return (
    estimateOffsetBoxFixedWidth(fontSize) +
    estimateOffsetBoxTextWidth(
      formatProductGroupsChartValue(widthScaleReference),
      fontSize,
    )
  );
}

/**
 * OffsetBox width in scaling units — scales linearly with {@link absValue} relative
 * to {@link widthScaleReference} (e.g. 60 vs 15 → 4× width).
 */
export function estimateOffsetBoxScaledWidth(
  absValue: number,
  widthScaleReference: number,
  fontSize: number,
): number {
  const baseWidth = estimateOffsetBoxBaseWidth(widthScaleReference, fontSize);
  const scaled = baseWidth * (absValue / widthScaleReference);
  const minContentWidth =
    estimateOffsetBoxFixedWidth(fontSize) +
    estimateOffsetBoxTextWidth(
      formatProductGroupsChartValue(absValue),
      fontSize,
    );
  return Math.max(scaled, minContentWidth);
}

/** Hover tooltip for a product-groups donut slice or legend row. */
export function formatProductGroupSegmentTooltip(
  segment: Pick<
    ProductGroupSegment,
    'label' | 'cloudValue' | 'arrowValue' | 'detailLabel'
  >,
): string {
  const heading = segment.detailLabel
    ? `${segment.label} (${segment.detailLabel})`
    : segment.label;
  const valuePart = `${formatProductGroupsChartValue(segment.cloudValue)} kgCO2e per m²`;
  if (
    segment.arrowValue == null ||
    Math.round(Math.abs(segment.arrowValue) * 10) === 0
  ) {
    return `${heading}: ${valuePart}`;
  }

  const offsetValue = -segment.arrowValue;
  const direction = offsetValue > 0 ? 'above' : 'below';
  return `${heading}: ${valuePart} (${formatProductGroupsChartValue(Math.abs(offsetValue))} kgCO2e per m² ${direction} baseline)`;
}

/**
 * Merges the smallest segments into a single "Other" group.
 *
 * Two kinds of segments are always forced into the group regardless of size:
 * {@link ElementCategoryID.OtherProduct} and any segment whose share of the
 * total is below {@link OVRIGA_MIN_SHARE} (< 1%). Additional segments are
 * added smallest-first as long as the combined group value stays within
 * {@link OVRIGA_MAX_SHARE} of the total. Grouping is skipped when no forced
 * members exist and fewer than two cap-qualified segments qualify.
 *
 * The merged segment's label lists its members in ascending size order, with
 * "&" replacing the final comma.
 */
export function mergeSmallSegmentsIntoOvriga(
  segments: ProductGroupSegment[],
): ProductGroupSegment[] {
  if (segments.length <= 1) return segments;

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const cap = total * OVRIGA_MAX_SHARE;

  const tinyThreshold = total * OVRIGA_MIN_SHARE;
  const isForced = (s: ProductGroupSegment) =>
    s.id === ElementCategoryID.OtherProduct || s.value < tinyThreshold;

  const forcedMembers = segments.filter(isForced);
  const capCandidates = segments.filter((s) => !isForced(s));

  const ascending = [...capCandidates].sort((a, b) => a.value - b.value);

  let accumulated = forcedMembers.reduce((sum, m) => sum + m.value, 0);
  const capMembers: ProductGroupSegment[] = [];

  for (const segment of ascending) {
    if (accumulated + segment.value <= cap) {
      accumulated += segment.value;
      capMembers.push(segment);
    } else {
      break;
    }
  }

  const members = [...forcedMembers, ...capMembers];
  if (members.length === 0) return segments;
  if (members.length < 2 && forcedMembers.length === 0) return segments;

  const memberIds = new Set(members.map((m) => m.id));
  const remaining = segments.filter((s) => !memberIds.has(s.id));

  const sortedForLabel = [...members].sort((a, b) => a.value - b.value);
  const label = buildOvrigaLabel(sortedForLabel);
  const color = members[members.length - 1]!.color;
  const arrowTotal =
    Math.round(members.reduce((sum, m) => sum + (m.arrowValue ?? 0), 0) * 10) /
    10;
  const hasArrow = members.some((m) => m.arrowValue != null);

  const ovriga: ProductGroupSegment = {
    id: OVRIGA_SEGMENT_ID,
    label: OVRIGA_SEGMENT_LABEL,
    detailLabel: label,
    memberIds: sortedForLabel.map((member) => member.id),
    value: members.reduce((sum, m) => sum + m.value, 0),
    cloudValue: members.reduce((sum, m) => sum + m.cloudValue, 0),
    ...(hasArrow ? { arrowValue: arrowTotal } : {}),
    color,
  };

  return [...remaining, ovriga];
}

/**
 * Segments shown in the product-groups donut and legend — baseline share > 0,
 * small baseline slices merged into "Other", sorted largest-first with Other last.
 */
export function prepareProductGroupsDisplaySegments(
  segments: ProductGroupSegment[],
): ProductGroupSegment[] {
  const filtered = segments.filter((segment) => segment.value > 0);
  if (filtered.length === 0) return [];

  const merged = mergeSmallSegmentsIntoOvriga(filtered);
  return [...merged].sort((a, b) => {
    if (a.id === OVRIGA_SEGMENT_ID) return 1;
    if (b.id === OVRIGA_SEGMENT_ID) return -1;
    return b.value - a.value;
  });
}

/** "A, B & C" – members listed in ascending size order. */
function buildOvrigaLabel(members: ProductGroupSegment[]): string {
  const names = members.map((m) => m.label);
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}
